import React, { useContext } from 'react';
import { BaseRendererProps } from './eventRenderers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTurnUp } from '@fortawesome/free-solid-svg-icons';
import GameAssetsContext from '../../../../../../context/GameAssetContext';
import { getUniversalSourceIcon } from './StatChangeEvent';
import './RenderAggregatedResourceEvent.css';

const RenderAggregatedResourceEvent: React.FC<BaseRendererProps> = ({ log }) => {
  const { cumulativeResourceCost, tickCount, totalResourceAmount, resourceType } = log.details;

  // Debug logging
  console.log('[DEBUG] RenderAggregatedResourceEvent called with:', {
    logType: log.type,
    cumulativeResourceCost,
    tickCount,
    totalResourceAmount,
    resourceType,
    details: log.details
  });

  const { gameWideAssets, championsAssets } = useContext(GameAssetsContext);

  const details = log.details;
  const individualCost = Math.round(details.individualResourceCost || 0);
  const totalCost = Math.round(cumulativeResourceCost || 0);
  const sourceType = details.sourceType || 'Ability';
  const sourceName = details.source;
  const imageState = log.imageState;
  const championName = details.championName;
  const abilityKey = details.spellKey;

  // Get the champion's maximum resource amount for accurate percentage calculation
  // totalResourceAmount should come from the champion's actual max resource stat
  const maxResourceAmount = details.maxResourceAmount || totalResourceAmount || 100;
  
  // Calculate percentage of max resources used
  const percentageUsed = maxResourceAmount ? ((totalCost / maxResourceAmount) * 100).toFixed(1) : '0';

  // Determine the resource icon based on resource type - using same classes as StatChangeEvent
  let resourceIcon = null;
  switch (resourceType?.toLowerCase()) {
    case 'mana':
      resourceIcon = <div className="stat-icon resource-icon"></div>;
      break;
    case 'energy':
      resourceIcon = <div className="stat-icon energy-icon"></div>;
      break;
    case 'health':
      resourceIcon = <div className="stat-icon health-icon"></div>;
      break;
    default:
      resourceIcon = <div className="stat-icon resource-icon"></div>; // Default to mana icon
  }

  // Use the universal source icon from StatChangeEvent
  const sourceIcon = getUniversalSourceIcon(
    {
      sourceType: sourceType,
      imageState: imageState,
      sourceName: sourceName,
      championName: championName,
      abilityKey: abilityKey,
    },
    gameWideAssets,
    championsAssets
  );

  // Display format - main amount and percentage only
  const mainAmountDisplay = `-${totalCost}`;
  const percentageDisplay = `(${percentageUsed}%)`;
  
  // Tooltip text for hover - show aggregation details if multiple occurrences
  const tooltipText = tickCount > 1 
    ? `Resource Usage: ${tickCount} × -${individualCost} = -${totalCost} (${percentageUsed}% of max resources)`
    : `Resource Usage: -${totalCost} (${percentageUsed}% of max resources)`;

  return (
    <div className="aggregated-resource-event stat-change-event" title={tooltipText}>
      <div className="stat-change-amount-wrapper">
        {resourceIcon}
        <div className="stat-change-amounts">
          <span className="stat-change-amount">{mainAmountDisplay}</span>
          <span className="stat-change-percentage">{percentageDisplay}</span>
        </div>
      </div>
      {sourceIcon && (
        <div className="stat-change-source-wrapper">
          <FontAwesomeIcon icon={faTurnUp} className="fa-turn-up" />
          {sourceIcon}
        </div>
      )}
    </div>
  );
};

export default RenderAggregatedResourceEvent; 